/**
 * certTiers — which subscription tier each AWS cert gates to.
 *
 * CERT_TIER_MAP mirrors the canonical map in
 * skills-platform/src/data/certTiers.ts. Also duplicated in:
 *   - aws-exam-app/infra/lambda/certTiers.mjs
 *   - aws-exam-app/infra/lambda-billing (by reference, no shared file)
 * Update all of them together when adding a cert.
 *
 * The Professional plan is retired platform-side, so no cert requires 'pro'
 * any more — Student unlocks the entire catalogue. CLF-C02 and AIF-C01 are
 * fully free tracks, quizzes and exams included, so a new account can finish a
 * real certification path before deciding to pay.
 */
export const CERT_TIER_MAP = {
  // ── Free (fully free tracks) ────────────────────────────────────────────
  'CLF-C02': 'free',
  'AIF-C01': 'free',

  // ── Student (everything else — includes the old Pro certs) ─────────────
  'SAA-C03': 'student',
  'DVA-C02': 'student',
  'SOA-C03': 'student',
  'SOA-C02': 'student',   // exam-app only; not in the platform catalogue
  'DEA-C01': 'student',
  'MLA-C01': 'student',
  'ANS-C01': 'student',
  'SCS-C02': 'student',
  'SCS-C03': 'student',   // exam-app only; not in the platform catalogue
  'SAP-C02': 'student',
  'DOP-C02': 'student',
  'AIP-C01': 'student',
};

/**
 * Tier ranks. DELIBERATELY NOT identical to the platform's TIER_RANK, which
 * ranks 'pro' EQUAL to 'student' because it retired Professional as a product.
 *
 * Here 'pro' is not a product at all — it is the internal "all-access" sentinel
 * that getUserEntitlement()/the billing Lambda assign to owners, open beta, the
 * grandfathered bundle, and a paid Learning Platform bundle. hasAccessToCert()
 * detects all-access with `TIER_RANK[userTier] >= TIER_RANK['pro']`, so 'pro'
 * MUST outrank 'student'.
 *
 * Flattening it to match the platform would make that comparison true for
 * tier 'student' — which is what a per-cert $14.99/mo exam subscriber is
 * assigned — handing one cert's buyer the entire catalogue. Leave pro at 2.
 */
export const TIER_RANK = {
  free: 0,
  student: 1,
  pro: 2,
};

export const OWNER_EMAILS = ['jossai@dreamandbuildllc.com'];

export const TIER_LABELS = {
  free: 'Free',
  student: 'Student',
  pro: 'Professional',
};

/**
 * Tier required to reach a cert's question bank.
 *
 * Unknown certs default to 'student', matching the canonical map: a cert nobody
 * has tagged yet stays behind a paid plan rather than opening up by accident.
 * This used to default to 'pro', which described a plan that no longer exists.
 */
export function tierForCert(certCode) {
  if (!certCode) return 'student';
  const upper = String(certCode).toUpperCase();
  return CERT_TIER_MAP[upper] || 'student';
}

/**
 * Check whether the user can access this cert.
 *
 * Per-cert subscription model: a $14.99/mo sub unlocks ONE cert at a time.
 * The user's `ownedCerts` set lists the cert codes they actively pay for.
 *
 *   - Owner email => true (always allowed).
 *   - opts.betaMode (skills-platform free open beta) => true for every cert.
 *   - legacyAllAccess (grandfathered $19.99 bundle, OR a paid Learning Platform
 *     bundle — the billing Lambda normalises both to legacyAllAccess) => true.
 *   - Platform 'pro' tier => true (cross-app passthrough).
 *   - Per-cert: true iff certCode is in ownedCerts.
 *   - Free certs (CLF-C02) => true for everyone.
 *
 * Backwards-compat: when ownedCerts is absent OR empty we fall back to the
 * tier-rank check, so a user whose entitlement comes from their tier rather
 * than a per-cert purchase is never locked out.
 */
export function hasAccessToCert(userTier, certCode, email, opts) {
  if (OWNER_EMAILS.includes(email)) return true;
  // Free open beta — nothing is for sale, so every cert is open. Defence in
  // depth only: the backend already reports tier='pro' + legacyAllAccess when
  // beta is on, and the backend is what actually guards the question banks.
  if (opts && opts.betaMode) return true;
  const required = tierForCert(certCode);
  if (required === 'free') return true;

  // An EMPTY ownedCerts array means "this user owns no per-cert subs", which is
  // not the same as "per-cert data is unavailable" — only the latter should skip
  // the tier-rank fallback below. This used to read `Array.isArray(...) ? ... :
  // null`, and since `[]` is truthy the per-cert branch always won and returned
  // false, making the fallback unreachable for anyone entitled by tier alone.
  const ownedCertsRaw = (opts && Array.isArray(opts.ownedCerts)) ? opts.ownedCerts : null;
  const ownedCerts = ownedCertsRaw && ownedCertsRaw.length > 0 ? ownedCertsRaw : null;
  const legacyAllAccess = !!(opts && opts.legacyAllAccess);

  if (legacyAllAccess) return true;
  // Platform pro is treated as all-access since the platform sub is a
  // single-tier bundle, not a per-cert product.
  if (TIER_RANK[userTier] >= TIER_RANK['pro']) return true;

  if (ownedCerts) {
    const upper = String(certCode || '').toUpperCase();
    if (ownedCerts.includes(upper)) return true;
    return false;
  }

  // Pre-migration fallback: simple tier-rank check.
  return TIER_RANK[userTier] >= TIER_RANK[required];
}
